import {testIdProps} from '@github-ui/test-id-props'
import {useMultiRef} from '@github-ui/use-multi-ref'
import {CopyIcon, PlusIcon, ProjectIcon, ProjectRoadmapIcon, TableIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu} from '@primer/react'
import {TabNav} from '@primer/react/deprecated'
import {clsx} from 'clsx'
import {memo, type MutableRefObject, startTransition, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {TabNavigationUI, ViewOptionsMenuUI} from '../api/stats/contracts'
import {ViewTypeParam} from '../api/view/contracts'
import {getInitialState} from '../helpers/initial-state'
import {shortcutFromEvent, SHORTCUTS} from '../helpers/keyboard-shortcuts'
import {scrollToChild} from '../helpers/scroll-to-child'
import {ViewerPrivileges} from '../helpers/viewer-privileges'
import {DragDropWithIdsContext, type OnDropWithIds, useDropWithIds} from '../hooks/drag-and-drop/drag-and-drop'
import {useElementIsOverflowing} from '../hooks/use-element-is-overflowing'
import {useMemexRootHeight} from '../hooks/use-memex-root-height'
import {ViewOptionsStatsUiContext} from '../hooks/use-view-options-stats-ui-key'
import {useViews} from '../hooks/use-views'
import {isWebkit} from '../platform/user-agent'
import {useProjectRouteParams} from '../router/use-project-route-params'
import {PROJECT_VIEW_ROUTE} from '../routes'
import {usePrefetchIssueFieldsQuery} from '../state-providers/issue-fields/use-issue-fields-query'
import {useUserNotices} from '../state-providers/user-notices/user-notices-provider'
import {Resources} from '../strings'
import {PotentiallyDirty} from './potentially-dirty'
import {NavLinkWithoutActiveClassName} from './react-router/link-without-active-classname'
import {MemexIssueTypeRenamePopover} from './user-notices/memex-issue-type-rename-popover'
import styles from './view-navigation.module.css'
import {ViewOptionsMenu} from './view-options/menu'
import {MoveTabDialog, MoveTabDialogContext, type MoveTabDialogProps} from './view-options/move-tab'
import {ViewTab} from './view-tab'

interface ViewNavigationProps {
  projectViewId: string
  onFocusIntoCurrentView: () => void
}

export const ViewNavigation = (props: ViewNavigationProps) => {
  const [moveTabDialogProps, setMoveTabDialogProps] = useState<MoveTabDialogProps | null>(null)

  return (
    <MoveTabDialogContext value={useMemo(() => ({setMoveTabDialogProps}), [setMoveTabDialogProps])}>
      <DragDropWithIdsContext>
        {moveTabDialogProps && (
          <MoveTabDialog
            close={() => {
              const returnRef = moveTabDialogProps.returnRef
              setMoveTabDialogProps(null)
              returnRef.current?.focus()
            }}
            {...moveTabDialogProps}
          />
        )}
        <ViewNavigationInner {...props} />
      </DragDropWithIdsContext>
    </MoveTabDialogContext>
  )
}

const ViewNavigationInner = memo(function ViewNavigationInner({
  projectViewId,
  onFocusIntoCurrentView,
}: ViewNavigationProps) {
  const {views, viewsMap, currentView, setCurrentViewNumber, updateViewPriority} = useViews()
  const {
    projectLimits: {viewsLimit},
  } = getInitialState()
  const {hasWritePermissions} = ViewerPrivileges()
  const {userNotices} = useUserNotices()
  const tabContainerRef = useRef<HTMLDivElement | null>(null)
  const viewOptionMenuTriggerRef = useRef<HTMLButtonElement>(null)
  const viewOptionMenuPlaceholderRef = useRef<HTMLDivElement | null>(null)
  const viewOptionMenuRef = useRef<HTMLDivElement | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const prefetchIssueFields = usePrefetchIssueFieldsQuery()
  const isWebKit = isWebkit()

  const hideViewOptionsMenu = useCallback(() => {
    if (viewOptionMenuRef.current) {
      viewOptionMenuRef.current.style.display = 'none'
      viewOptionMenuRef.current.style.left = '0px'
    }
    if (viewOptionMenuPlaceholderRef.current) {
      viewOptionMenuPlaceholderRef.current.style.visibility = 'visible'
      viewOptionMenuPlaceholderRef.current.style.opacity = '1'
    }
  }, [])

  const showViewOptionsMenu = useCallback(() => {
    // We need to wait until the next tick to show the menu, so that final position of the placeholder is calculated.
    // Not sure why exactly, but for Safari, we need to use requestAnimationFrame to get the correct position,
    // whereas for Chrome and Safari, setTimeout gives the correct position.
    const onNextTick = isWebKit ? requestAnimationFrame : setTimeout
    onNextTick(() => {
      if (viewOptionMenuPlaceholderRef.current && viewOptionMenuRef.current && scrollContainerRef.current) {
        viewOptionMenuRef.current.style.display = 'flex'
        viewOptionMenuRef.current.style.left = `${
          scrollContainerRef.current.scrollLeft -
          scrollContainerRef.current.getBoundingClientRect().left +
          viewOptionMenuPlaceholderRef.current.getBoundingClientRect().left
        }px`
        viewOptionMenuPlaceholderRef.current.style.visibility = 'hidden'
        viewOptionMenuPlaceholderRef.current.style.opacity = '0'
      }
    })
  }, [isWebKit])

  // Only update menu position when tab structure changes (number or order of views)
  const tabStructure = views.map(v => v.id).join(',')
  useEffect(() => {
    hideViewOptionsMenu()
    showViewOptionsMenu()
  }, [hideViewOptionsMenu, showViewOptionsMenu, views.length, tabStructure])

  // Update position of menu when views change on resize
  useMemexRootHeight({
    onResize: () => {
      hideViewOptionsMenu()
      showViewOptionsMenu()
    },
  })

  const onDrop = useCallback<OnDropWithIds<{id: number; number: number}>>(
    async ({dragMetadata, dropMetadata, side}) => {
      if (!hasWritePermissions || !dropMetadata || !dragMetadata) return

      const draggedView = viewsMap[dragMetadata.number]
      const indexOfDraggedView = views.findIndex(view => view.id === dragMetadata?.id)
      const indexOfDroppedView = views.findIndex(view => view.id === dropMetadata?.id)

      if (!draggedView || indexOfDraggedView === indexOfDroppedView) return

      /**
       When views that already exist are backfilled with priorities, they receive priorities in the reverse order
       in which they were originally placed.

       For example:
       1. views - [1, 2, 3, 4] (no priorities)
       2. backfill run
       3, prioritized_views - [4, 3, 2, 1]

       We return `prioritized_views.reversed` from the backend in the `memexViews` JSON island in order to maintain
       the same view order for clients before and after the backfill.

       This changes the interpretation of "previousMemexProjectViewId" on the front end

       For a list sorted in ascending order of priority, `previousMemexProjectViewId` is the ID of the view *before*
       which we want to insert the dragged view.

       For example: views on the front end = [1, 2, 3, 4]
       - To shift 1 to be between 3 and 4, `previousMemexProjectViewId` should be 4
       - To shift 1 to be the last item, `previousMemexProjectViewId` should be empty
      */

      // the view *before* which we want to drop the dragged view
      let targetView

      if (side === 'before') {
        targetView = views[indexOfDroppedView]
      } else if (side === 'after' && indexOfDroppedView !== views.length - 1) {
        targetView = views[indexOfDroppedView + 1]
      }

      if (draggedView.number === targetView?.number) return // if dragged view is moving to its own position
      // the view *currently* after the view being dragged view
      // This will be used to revert local state changes if the API call to update the priority fails
      const currentViewAfter = indexOfDraggedView !== views.length - 1 ? views[indexOfDraggedView + 1] : undefined

      await updateViewPriority(draggedView.number, targetView?.id || '', targetView?.number, currentViewAfter?.number)
      showViewOptionsMenu()
    },
    [hasWritePermissions, viewsMap, views, updateViewPriority, showViewOptionsMenu],
  )

  const drop = useDropWithIds({
    dropID: 'views',
    dropType: 'view',
    dropRef: tabContainerRef,
    onDrop,
  })

  const onSelectedTabKeyDown = useCallback<React.KeyboardEventHandler>(
    event => {
      if (shortcutFromEvent(event) === SHORTCUTS.ARROW_DOWN) {
        event.preventDefault()
        onFocusIntoCurrentView()
      }
    },
    [onFocusIntoCurrentView],
  )

  const tabRefs = useMultiRef<number, HTMLAnchorElement>()

  useEffect(() => {
    const currentTabLink = currentView && tabRefs(currentView.number).current
    const scrollContainer = scrollContainerRef.current

    if (currentTabLink && scrollContainer) scrollToChild(scrollContainer, currentTabLink, {direction: 'x'})
  }, [tabRefs, currentView])

  // Convert vertical wheel scroll to horizontal scroll on tab bar
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const handleWheel = (event: WheelEvent) => {
      // Ignore pinch-to-zoom gestures (ctrlKey) and events without vertical scroll
      // Allows zooming to work even when cursor is over the tab bar
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- detecting pinch-to-zoom, not a keyboard shortcut
      if (event.ctrlKey || event.deltaY === 0) return

      if (container.scrollWidth > container.clientWidth) {
        event.preventDefault()
        container.scrollBy({left: event.deltaY})
      }
    }

    // eslint-disable-next-line github/require-passive-events -- needed to prevent default vertical scroll
    container.addEventListener('wheel', handleWheel, {passive: false})
    return () => container.removeEventListener('wheel', handleWheel)
  }, [])

  const setViewOptionsPlaceholderRef = useCallback(
    (viewOptionMenuPlaceholder: HTMLDivElement) => {
      viewOptionMenuPlaceholderRef.current = viewOptionMenuPlaceholder
      showViewOptionsMenu()
    },
    [showViewOptionsMenu],
  )

  const renamePopoverAnchorRef: MutableRefObject<HTMLElement | null> = viewOptionMenuRef

  return (
    <div
      onFocus={prefetchIssueFields}
      onMouseEnter={prefetchIssueFields}
      ref={scrollContainerRef}
      className={styles.ViewNavigationContainer}
    >
      <div {...drop.props} ref={tabContainerRef} className={styles.tabsContainer}>
        <TabNav aria-label="Select view" className={styles.viewTabNav} {...testIdProps('tab-nav')}>
          {views.map(view => {
            const selected = view.number === currentView?.number
            return (
              <ViewNavigationTab
                key={view.number}
                ref={tabRefs(view.number)}
                selected={selected}
                projectViewId={projectViewId}
                onTabChange={(e: React.MouseEvent) => {
                  // prevent link from trigger navigation, as view state will do this, but show an href
                  e.preventDefault()
                  if (selected) return
                  setCurrentViewNumber(view.number, {ui: TabNavigationUI})
                  hideViewOptionsMenu()
                }}
                view={view}
                onKeyDown={selected ? onSelectedTabKeyDown : undefined}
                setViewOptionsPlaceholderRef={setViewOptionsPlaceholderRef}
                viewOptionMenuTriggerRef={viewOptionMenuTriggerRef}
                showViewOptionsMenu={showViewOptionsMenu}
                hideViewOptionsMenu={hideViewOptionsMenu}
              />
            )
          })}
          {hasWritePermissions && views.length < viewsLimit && <NewViewMenu />}
        </TabNav>
      </div>
      {currentView ? (
        <>
          <ViewOptionsStatsUiContext value={ViewOptionsMenuUI}>
            <div
              ref={viewOptionMenuRef}
              className={styles.viewOptionsMenuContainer}
              {...testIdProps('view-options-menu')}
            >
              <ViewOptionsMenu
                viewOptionMenuTriggerRef={viewOptionMenuTriggerRef}
                view={currentView}
                showViewOptionsMenu={showViewOptionsMenu}
              />
            </div>
          </ViewOptionsStatsUiContext>
          {userNotices.memex_issue_types_rename_prompt ? (
            <MemexIssueTypeRenamePopover anchorRef={renamePopoverAnchorRef} />
          ) : null}
        </>
      ) : null}
    </div>
  )
})

function NewViewMenu() {
  const {createNewDefaultView, duplicateCurrentViewState, currentView} = useViews()

  return (
    <ActionMenu>
      <ActionMenu.Button
        {...testIdProps('view-navigation-create-new-view')}
        role="tab"
        variant="invisible"
        leadingVisual={PlusIcon}
        trailingAction={null}
        className={styles.newViewButton}
      >
        New view
      </ActionMenu.Button>
      <ActionMenu.Overlay {...testIdProps('view-navigation-create-new-view-overlay')}>
        <ActionList>
          <ActionList.Group>
            <ActionList.GroupHeading>Layout</ActionList.GroupHeading>
            <ActionList.Item
              onSelect={() => createNewDefaultView({layout: ViewTypeParam.Table}, {ui: TabNavigationUI})}
            >
              <ActionList.LeadingVisual>
                <TableIcon />
              </ActionList.LeadingVisual>
              Table
            </ActionList.Item>
            <ActionList.Item
              onSelect={() => createNewDefaultView({layout: ViewTypeParam.Board}, {ui: TabNavigationUI})}
            >
              <ActionList.LeadingVisual>
                <ProjectIcon />
              </ActionList.LeadingVisual>
              Board
            </ActionList.Item>
            <ActionList.Item
              onSelect={() => createNewDefaultView({layout: ViewTypeParam.Roadmap}, {ui: TabNavigationUI})}
            >
              <ActionList.LeadingVisual>
                <ProjectRoadmapIcon />
              </ActionList.LeadingVisual>
              Roadmap
            </ActionList.Item>
          </ActionList.Group>
          {currentView ? (
            <>
              <ActionList.Divider />
              <ActionList.Item
                onSelect={() => {
                  duplicateCurrentViewState(currentView.number, undefined, {ui: TabNavigationUI})
                }}
              >
                <ActionList.LeadingVisual>
                  <CopyIcon />
                </ActionList.LeadingVisual>
                {Resources.duplicateView({isDirty: currentView.isViewStateDirty})}
              </ActionList.Item>
            </>
          ) : null}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

/**
 * A wrapper component that avoids passing sx to the rendered NavLink
 */
function TabNavLink({ref, ...props}: React.ComponentProps<typeof NavLinkWithoutActiveClassName>) {
  return <NavLinkWithoutActiveClassName {...props} ref={ref} />
}

interface ViewNavigationTabProps {
  ref?: React.Ref<HTMLAnchorElement>
  selected: boolean
  onTabChange: React.MouseEventHandler
  view: ReturnType<typeof useViews>['views'][number]
  projectViewId: string
  viewOptionMenuTriggerRef: React.RefObject<HTMLButtonElement | null>
  setViewOptionsPlaceholderRef: (placeholder: HTMLDivElement) => void
  hideViewOptionsMenu: () => void
  showViewOptionsMenu: () => void
  onKeyDown?: React.KeyboardEventHandler
}

function ViewNavigationTab({
  ref,
  selected,
  onTabChange,
  view,
  projectViewId,
  viewOptionMenuTriggerRef,
  setViewOptionsPlaceholderRef,
  hideViewOptionsMenu,
  showViewOptionsMenu,
  onKeyDown,
}: ViewNavigationTabProps) {
  const {hasWritePermissions} = ViewerPrivileges()
  const tabRef = useRef<HTMLSpanElement>(null)

  /**
   *
   * We don't use react-router links here, since
   * view urls are currently managed from internal
   * state.
   */
  const isOverflowing = useElementIsOverflowing(tabRef)
  const projectRouteParams = useProjectRouteParams()
  return (
    <TabNav.Link
      ref={ref}
      className={clsx(
        isOverflowing && !selected && styles.truncatedTabLink,
        styles.viewTabLink,
        selected && styles.selectedTabLink,
        isOverflowing && !selected && styles.overflowingTabLink,
      )}
      selected={selected}
      aria-selected={selected ? 'true' : undefined}
      as={TabNavLink}
      aria-controls={selected ? projectViewId : undefined}
      to={selected ? undefined : PROJECT_VIEW_ROUTE.generatePath({...projectRouteParams, viewNumber: view.number})}
      title={view.name}
      onKeyDown={onKeyDown}
      onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
        // If any modifier keys are held, don't change the tab
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
        // If the primary mouse button is not clicked, don't change the tab
        if (event.button !== 0) return

        startTransition(() => {
          onTabChange(event)
        })
      }}
      {...testIdProps('view-navigation-view-tab-link')}
    >
      <ViewTab
        ref={tabRef}
        view={view}
        selected={selected}
        viewOptionMenuTriggerRef={viewOptionMenuTriggerRef}
        hideViewOptionsMenu={hideViewOptionsMenu}
        showViewOptionsMenu={showViewOptionsMenu}
        setViewOptionsPlaceholderRef={setViewOptionsPlaceholderRef}
      />
      {view.isViewStateDirty && !selected && (
        <PotentiallyDirty
          key={`dirty-for-${view.number}`}
          isDirty
          hideDirtyState={!hasWritePermissions}
          className={styles.dirtyIndicator}
        />
      )}
    </TabNav.Link>
  )
}
